import { HttpClient } from "@angular/common/http";
import { computed, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { DialogsService } from "app/shared/services/dialogs/dialogs.service";
import { SnackService } from "app/shared/services/snack-service.service";
import { environment } from "environments/environment";
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";
import { AdministrationService } from "../../administration/services/administration-service.service";
import { LoginDialogComponent } from "../containers/login-dialog/login-dialog.component";
import { Role } from "../enums/role.enum";
import { Credentials } from "../models/credentials.model";
import { User } from "../models/user.model";
import { LocalStorageService } from "app/shared/services/local-storage.service";
import * as Bowser from "bowser";
import { HydrateService } from "app/shared/services/hydrate.service";
import { SentryService } from "../../logs/services/sentry.service";
import { UiFacadeService } from "../../user_infos/services/store/ui-facade.service";
import { SSOTokens } from "../models/sso-tokens.model";
import { JustupOffre } from "../../activity_infos/justup-offre.enum";
import { safeFirstValueFrom } from "../../../shared/utils/safe-first-value-from";
import { GeneralSocketService } from "../../../shared/services/general-socket.service";
import { ActivityInfosService } from "../../activity_infos/services/activity_infos.service";
import { PassFacadeService } from "../../administration/services/pass-store/pass-facade.service";
import { Pass } from "../../administration/models/pass.model";
import { LanguageService } from "../../../shared/services/language.service";
import { AdresseFacadeService } from "../../adresse/services/component-store/adresse-facade.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  popupOpened: boolean = false;

  offreSignal = signal<JustupOffre>(JustupOffre.JUSTUP);
  ssoSignal = signal<SSOTokens>(null);
  tokenSignal = signal<string>(null);
  userSignal = signal<User>(null);

  private uiFacade = inject(UiFacadeService);
  private sentryService = inject(SentryService);
  private generalSocketService = inject(GeneralSocketService);
  private aiService = inject(ActivityInfosService);
  private passFacade = inject(PassFacadeService);
  private languageService = inject(LanguageService);
  private adresseFacade = inject(AdresseFacadeService);

  constructor(
    private http: HttpClient,
    private router: Router,
    private dialogsService: DialogsService,
    public administrationService: AdministrationService,
    private snackService: SnackService,
    private localStorageService: LocalStorageService,
    private hydrateService: HydrateService,
  ) {
    // Hydratation synchrone des signaux d'auth depuis localStorage AVANT
    // toute activation de route ou requête HTTP. AuthService est providedIn:
    // 'root' donc le constructor tourne à la création de l'injecteur racine
    // (avant le router et avant que les interceptors ne traitent une requête).
    // Sans ça, l'AuthInterceptor pouvait voir `tokenSignal()` à null sur la
    // première requête (déclenchée par un composant lazy avant que
    // HomeModule.constructor n'ait eu le temps de finir loadStoredUser) →
    // redirection vers /auth/login à chaque ouverture de fenêtre détachée.
    this.hydrateAuthFromStorageSync();
  }

  /**
   * Lecture synchrone du localStorage et restauration des signaux d'auth
   * (token, sso, offre, pass, userActive). Aucune requête HTTP — la partie
   * async (refresh SSO, loadInitData) reste dans `loadStoredUser()` appelée
   * par HomeModule au runtime.
   */
  private hydrateAuthFromStorageSync(): void {
    try {
      const storeUserString: string =
        this.localStorageService.getWithTTL("USER");
      if (!storeUserString) return;
      const storeUser: User = JSON.parse(storeUserString);
      const storeSSO: SSOTokens = this.localStorageService.getWithTTL("SSO");
      const offre: JustupOffre = this.localStorageService.getWithTTL("OFFRE");

      // Signaux d'auth critiques d'abord — ils débloquent l'AuthInterceptor
      // et le RouteGuard. Le reste (pass, offre) est best-effort.
      this.tokenSignal.set(storeUser.token);
      this.ssoSignal.set(storeSSO);
      this.userSignal.set(storeUser);
      this.userActive.set(true);

      if (offre) this.offreSignal.set(offre);
      const pass: Pass = this.localStorageService.getWithTTL("PASS");
      if (pass) {
        try {
          this.passFacade.store.addOne(pass);
        } catch (e) {
          // Best-effort — le pass sera réhydraté par loadInitData ; le
          // RouteGuard a un fallback localStorage.
          console.warn("[AuthService] hydrate pass facade failed", e);
        }
      }
    } catch (e) {
      console.warn("[AuthService] hydrateAuthFromStorageSync failed", e);
    }
  }
  private _compatibleBrowser$ = new BehaviorSubject<boolean>(null);

  get compatibleBrowser$(): Observable<boolean> {
    return this._compatibleBrowser$.asObservable();
  }

  isAdmin = computed(() => this.userSignal()?.role === Role.ADMIN);

  checkBrowserCompatibility() {
    const browser = Bowser.getParser(window.navigator.userAgent);
    if (window.location.href.includes("localhost")) {
      this._compatibleBrowser$.next(true);
    } else {
      this._compatibleBrowser$.next(
        browser.satisfies({
          chrome: ">=120",
          firefox: ">=120",
          edge: ">=120",
          safari: ">=17",
        }),
      );
    }
  }

  updateQrForUser(userId: string) {
    return this.http
      .post(`${environment.api()}/users/qr/${userId}`, null)
      .subscribe((res) => {
        this.snackService.info("QR code d'identification mit à jour");
      });
  }

  async login(credentials: Credentials) {
    const result = await safeFirstValueFrom(
      this.http.post<{
        access_token: string;
        role: Role;
        passId: string;
        userId: string;
        sso: SSOTokens;
        offre: JustupOffre;
      }>(`${environment.api()}/auth/login`, credentials),
    );
    if (!result) return;
    this.userActive.set(true);
    this.localStorageService.putWithTTL(
      "lastUserMail",
      credentials.username,
      60 * 60 * 14,
    );
    this.tokenSignal.set(result.access_token);
    const user = {
      username: credentials.username,
      id: result.userId,
      token: result.access_token,
      role: result.role,
      passId: result.passId,
      ssoId: result.sso.user.id,
    } as User;
    this.sentryService.configureSentry(user);

    this.localStorageService.putWithTTL("OFFRE", result.offre);
    this.localStorageService.putWithTTL("USER", JSON.stringify(user));
    this.localStorageService.putWithTTL("SSO", result.sso);

    this.userSignal.set(user);
    this.offreSignal.set(result.offre);
    this.ssoSignal.set(result.sso);
    await this.loadInitData();
  }

  async loadInitData() {
    await this.uiFacade.loadAll();
    await this.hydrateService.hydrateAll();
    await this.aiService.loadActivityInfos();
    await this.passFacade.loadAll();
    this.passFacade.getState().currentPassId.set(this.userSignal().passId);
    const pass = this.passFacade.currentPass();
    this.localStorageService.putWithTTL("PASS", pass);
    await this.uiFacade.loadCurrent();
    const currentUserInfos = this.uiFacade.currentUserInfos();
    if (currentUserInfos?.language) {
      this.languageService.setLanguage(currentUserInfos.language, false);
    }
    this.adresseFacade.loadAllForGroup();
    this.generalSocketService.connect();
    this.generalSocketService.start(this.tokenSignal());
  }

  async loginQrCode(qr: string) {
    const result = await firstValueFrom(
      this.http.post<{
        access_token: string;
        role: Role;
        passId: string;
        userId: string;
      }>(`${environment.api()}/auth/login/qr/${qr}`, null),
    );

    this.tokenSignal.set(result.access_token);
    const user = {
      id: result.userId,
      token: result.access_token,
      role: result.role,
      passId: result.passId,
    } as User;
    this.userSignal.set(user);
    await this.loadInitData();
  }

  askLogin() {
    const compatible = this._compatibleBrowser$.value;
    if (compatible && !this.popupOpened) {
      return this.dialogsService.open(
        LoginDialogComponent,
        "fit-content",
        "fit-content",
      );
    }
  }

  async loadStoredUser() {
    // L'hydratation synchrone des signaux est déjà faite dans le constructor
    // de l'AuthService (cf. hydrateAuthFromStorageSync). On ne refait que la
    // partie async ici : refresh SSO + chargement des données d'init + sentry.
    const storeUserString: string = this.localStorageService.getWithTTL("USER");
    if (!storeUserString) return;
    const storeUser: User = JSON.parse(storeUserString);
    await this.ensureRefreshSso();
    await this.loadInitData();
    this.sentryService.configureSentry(storeUser);
  }

  logout(): void {
    this.generalSocketService.disconnect();
    this.generalSocketService.stop();
    localStorage.removeItem("USER");
    localStorage.removeItem("SSO");
    localStorage.removeItem("OFFRE");
    localStorage.removeItem("PASS");
    this.stopAutoRefresh();
    this.userSignal.set(null);
    this.tokenSignal.set(null);
    this.userActive.set(false);
    this.router.navigateByUrl("/auth/login");
  }

  userActive = signal(false);

  isSoftLogout() {
    return !this.userActive();
  }

  softLogout() {
    this.stopAutoRefresh();
    this.userActive.set(false);
    this.askLogin();
  }

  isSsoExpiring() {
    if (!this.ssoSignal()?.tokenExpires) {
      return false;
    }
    const expire = new Date(this.ssoSignal().tokenExpires);
    const compare = new Date();
    return +expire < +compare;
  }

  async ensureRefreshSso() {
    if (!this.isSsoExpiring()) {
      return;
    }
    const sso = await firstValueFrom(
      this.http.post<SSOTokens>(`${environment.adminApi()}/auth/refresh`, null),
    );
    this.ssoSignal.set(sso);
    this.localStorageService.putWithTTL("SSO", sso);
  }

  private _refreshTimer?: any;

  startAutoRefresh() {
    this.stopAutoRefresh();
    const exp = this.ssoSignal()?.tokenExpires
      ? new Date(this.ssoSignal().tokenExpires).getTime()
      : 0;
    if (!exp) return;

    const delay = Math.max(1000, exp - Date.now() - 300 * 1000);
    this._refreshTimer = setTimeout(async () => {
      try {
        await this.ensureRefreshSso();
      } finally {
        this.startAutoRefresh();
      }
    }, delay);

    const onVisible = async () => {
      if (document.visibilityState === "visible" && this.isSsoExpiring()) {
        await this.ensureRefreshSso();
        this.startAutoRefresh();
      }
    };
    document.removeEventListener("visibilitychange", onVisible as any);
    document.addEventListener("visibilitychange", onVisible, { once: false });
  }

  stopAutoRefresh() {
    if (this._refreshTimer) clearTimeout(this._refreshTimer);
    this._refreshTimer = undefined;
  }
}
